/**
 * Types, constants, and defaults for the RFQ Create Wizard.
 */
import type {
  RFQCostType,
  RFQCertificateType,
  RFQCategory,
  RFQPriority,
  RFQContractType,
  RFQContractFrequency,
} from '../../lib/api';

export interface LineItemForm {
  item_description: string;
  part_number: string;
  quantity: number;
  unit_of_measure: string;
  target_unit_price: number | null;
  // CR-5467: drawing_issue / revision number is required for version
  // control on engineering drawings; surfaced at line level.
  drawing_issue: string;
  // CR-5467: delivery_location and required_delivery_date no longer
  // belong on the line item — they're RFQ-header-level concerns.
  // Kept on the type as optional so existing draft / response payloads
  // don't fall over until the header form ships in the follow-up.
  delivery_location?: string;
  required_delivery_date?: string;
  technical_specifications: string;
  notes: string;
}

export interface AdditionalCostForm {
  cost_type: RFQCostType;
  description: string;
  amount: number;
  notes: string;
}

export interface RequiredCertificateForm {
  certificate_type: RFQCertificateType;
  certificate_name: string;
  description: string;
  is_mandatory: boolean;
  expiry_tolerance_days: number;
}

export interface SupplierOption {
  id: number;
  company: number;
  company_name: string;
}

export interface BasicDetailsForm {
  title: string;
  description: string;
  category: RFQCategory;
  priority: RFQPriority;
  currency: string;
  response_deadline: string;
  validity_period_days: number;
  contract_type: RFQContractType;
  contract_frequency: RFQContractFrequency | null;
  contract_valid_from: string;
  contract_valid_to: string;
  terms_and_conditions: string;
  special_requirements: string;
  nda_required: boolean;
  auto_create_contract: boolean;
}

export interface WizardStep {
  id: number;
  title: string;
  description: string;
}

export const STEPS: WizardStep[] = [
  { id: 1, title: 'Basic Details', description: 'RFQ information and contract type' },
  { id: 2, title: 'Line Items', description: 'Parts and quantities required' },
  { id: 3, title: 'Additional Costs', description: 'Tooling, packaging, transport' },
  { id: 4, title: 'Documents & NDA', description: 'Attachments and certificates' },
  { id: 5, title: 'Suppliers', description: 'Select recipients' },
  { id: 6, title: 'Review & Send', description: 'Final review' },
];

export const EMPTY_LINE_ITEM: LineItemForm = {
  item_description: '',
  part_number: '',
  quantity: 1,
  unit_of_measure: 'Each',
  target_unit_price: null,
  drawing_issue: '',
  technical_specifications: '',
  notes: '',
};

export const EMPTY_COST: AdditionalCostForm = {
  cost_type: 'tooling',
  description: '',
  amount: 0,
  notes: '',
};

export const EMPTY_CERTIFICATE: RequiredCertificateForm = {
  certificate_type: 'iso_9001',
  certificate_name: '',
  description: '',
  is_mandatory: true,
  expiry_tolerance_days: 90,
};

export const DEFAULT_BASIC_DETAILS: BasicDetailsForm = {
  title: '',
  description: '',
  category: 'materials',
  priority: 'medium',
  currency: 'GBP',
  response_deadline: '',
  validity_period_days: 30,
  contract_type: 'one_time',
  contract_frequency: null,
  contract_valid_from: '',
  contract_valid_to: '',
  terms_and_conditions: '',
  special_requirements: '',
  nda_required: false,
  auto_create_contract: true,
};
